import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

const ACCENT_COLORS = [
  { border: 'border-teal-500/40',   bg: 'bg-teal-500/10',   text: 'text-teal-300',   pill: 'bg-teal-500/20 text-teal-300 border-teal-500/30' },
  { border: 'border-blue-500/40',   bg: 'bg-blue-500/10',   text: 'text-blue-300',   pill: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  { border: 'border-purple-500/40', bg: 'bg-purple-500/10', text: 'text-purple-300',  pill: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  { border: 'border-green-500/40',  bg: 'bg-green-500/10',  text: 'text-green-300',   pill: 'bg-green-500/20 text-green-300 border-green-500/30' },
  { border: 'border-yellow-500/40', bg: 'bg-yellow-500/10', text: 'text-yellow-300',  pill: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
];

function PlaybookCard({ playbook, idx, stepsDone, onStepToggle, onAskAI, promptPack, onGoToPrompts }) {
  const [expanded, setExpanded] = useState(idx === 0);
  const acc = ACCENT_COLORS[idx % ACCENT_COLORS.length];
  const toolNames = (playbook.tools || [])
    .map(t => t.tool_name || t.name || '')
    .filter(Boolean)
    .slice(0, 4);

  const playbookId = playbook.id || `pb-${idx}`;
  const steps = playbook.steps || [];
  const allStepsDone = steps.length > 0 && steps.slice(0, 4).every((_, i) => !!stepsDone[`${playbookId}-${i}`]);

  const relatedPrompts = (promptPack || [])
    .filter(p =>
      p.playId === (playbook.id || playbook.name) ||
      playbook.tools?.some(t =>
        p.title?.toLowerCase().includes((t.tool_name || t.name || '').toLowerCase())
      )
    )
    .slice(0, 2);

  return (
    <div className={`rounded-xl border ${acc.border} bg-slate-900/60 overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/40 transition-colors"
      >
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${acc.bg} ${acc.text} border ${acc.border} flex-shrink-0`}>
            {idx + 1}
          </span>
          <div>
            <h3 className="font-semibold text-white text-sm">{playbook.name}</h3>
            <p className={`text-xs mt-0.5 ${acc.text}`}>{playbook.outcome}</p>
          </div>
        </div>
        <span className={`text-lg transition-transform ${expanded ? 'rotate-180' : ''} ${acc.text}`}>
          ▾
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-700/50 pt-4">
          {/* Problems addressed */}
          {(playbook.problemSummary || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Addresses</p>
              <div className="space-y-1">
                {playbook.problemSummary.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">●</span>
                    <span className="text-sm text-slate-300">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Launch steps */}
          {steps.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Launch Steps</p>
              <ol className="space-y-2">
                {steps.slice(0, 4).map((step, i) => {
                  const isDone = !!stepsDone[`${playbookId}-${i}`];
                  return (
                    <li key={i}
                      onClick={() => onStepToggle(playbookId, i)}
                      className="flex items-start gap-3 cursor-pointer group"
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 border transition-colors ${
                        isDone ? `${acc.bg} ${acc.text} ${acc.border}` : 'bg-slate-800 border-slate-600 text-slate-400'
                      }`}>
                        {isDone ? '✓' : i + 1}
                      </span>
                      <span className={`text-sm leading-relaxed transition-colors flex-1 ${
                        isDone ? 'line-through text-slate-500' : 'text-slate-300 group-hover:text-white'
                      }`}>
                        {step}
                      </span>
                      {onAskAI && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAskAI(`Help me complete this step for my business: "${step}". Give me exact actions, any prompts I should use, and what a good result looks like.`);
                          }}
                          className="flex-shrink-0 text-[10px] text-slate-500 hover:text-[#3b82f6] border border-white/8 hover:border-blue-500/30 rounded-full px-2 py-0.5 transition-colors whitespace-nowrap mt-0.5"
                        >
                          Ask AI →
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
              {allStepsDone && (
                <div className="mt-3 text-center py-2 bg-blue-500/10 border border-blue-500/25 rounded-lg text-xs text-[#3b82f6] font-semibold">
                  🎉 Playbook complete!
                </div>
              )}
            </div>
          )}

          {/* Tools used */}
          {toolNames.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Tools Used</p>
              <div className="flex flex-wrap gap-2">
                {toolNames.map((tn, i) => (
                  <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${acc.pill}`}>
                    {tn}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Related prompts */}
          {relatedPrompts.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Related Prompts</p>
              <div className="space-y-1.5">
                {relatedPrompts.map(p => (
                  <div key={p.id}
                    onClick={() => onGoToPrompts?.()}
                    className="flex items-center justify-between bg-slate-800/50 border border-white/8 rounded-lg px-3 py-2 cursor-pointer hover:border-white/15 transition-colors">
                    <span className="text-xs text-slate-300 truncate">{p.title}</span>
                    <span className="text-xs text-[#3b82f6] flex-shrink-0 ml-2">Use prompt →</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlaybooksTab({ report, onAskAI = null, promptPack = [], onGoToPrompts = null }) {
  const params = useParams();
  const reportId = params?.reportId || 'default';
  const playbooks = report?.playbooks || [];

  const [stepsDone, setStepsDone] = useState(() => {
    try {
      const saved = localStorage.getItem(`playbooksteps-${reportId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const handleStepToggle = (playbookId, stepIdx) => {
    setStepsDone(prev => {
      const key = `${playbookId}-${stepIdx}`;
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(`playbooksteps-${reportId}`, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  if (!playbooks.length) {
    return (
      <div className="text-center py-12 text-slate-500">
        No playbooks available for this report.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">AI Playbooks</h2>
        <p className="text-sm text-slate-400 mt-1">
          Step-by-step AI workflows tailored to your business — ready to launch.
        </p>
      </div>
      {playbooks.map((pb, idx) => (
        <PlaybookCard
          key={pb.id || idx}
          playbook={pb}
          idx={idx}
          stepsDone={stepsDone}
          onStepToggle={handleStepToggle}
          onAskAI={onAskAI}
          promptPack={promptPack}
          onGoToPrompts={onGoToPrompts}
        />
      ))}
    </div>
  );
}
